import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Section } from '../components/shared/Section';
import { SectionHeading } from '../components/shared/SectionHeading';
import { GlassCard } from '../components/shared/GlassCard';
import { Particles } from '../components/shared/Particles';
import { SEO } from '../components/shared/SEO';
import { SERVICES, PROCESS_STEPS, WHY_CHOOSE_US, CONTACT_INFO } from '../lib/constants';

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left - rect.width / 2) / 50,
          y: (e.clientY - rect.top - rect.height / 2) / 50,
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Parallax */}
      <motion.div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1550524756-d07d631274cc?auto=format&fit=crop&w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        animate={{
          x: mousePosition.x,
          y: mousePosition.y,
        }}
        transition={{ type: 'spring', stiffness: 100, damping: 30 }}
      />

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/70" />

      {/* Glow Effect */}
      <div className="hero-glow top-1/4 left-1/4" />
      <div className="hero-glow bottom-1/4 right-1/4" />

      {/* Particles */}
      <Particles count={30} />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-5xl">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-sm uppercase tracking-[0.3em] text-primary mb-6"
        >
          Audio & Video Production Studio
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="font-syne text-4xl sm:text-5xl md:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6"
        >
          Audio & Video Production Studio in Guwahati, Assam
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="font-manrope text-base md:text-lg text-muted-foreground max-w-3xl mx-auto mb-8"
        >
          Professional{' '}
          <Link
            to="/music-video-production-guwahati"
            className="text-primary underline"
          >
            music video production in Guwahati
          </Link>
          , brand films, commercial video shoots, audio recording, mixing &
          mastering, editing and cinematic post-production services serving
          clients across Guwahati and Assam.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/contact">
            <Button
              data-testid="hero-cta-primary"
              size="lg"
              className="btn-glow bg-primary hover:bg-primary/90 text-white font-manrope px-8 py-6 text-base"
            >
              Get a Quote
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-3 bg-white/50 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

/* ========================= ABOUT ========================= */

const AboutPreviewSection = () => {
  return (
    <Section id="about">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-sm uppercase tracking-widest text-primary mb-4">
            About Hydrox Studios
          </p>

          <h2 className="font-syne text-4xl md:text-5xl font-bold text-white mb-6">
            Structured Creative Production in Guwahati
          </h2>

          <p className="text-muted-foreground mb-6">
            Hydrox Studios is a Guwahati-based audio and video production studio
            built on disciplined workflows and cinematic execution. From music
            videos to brand films and audio post-production, every project is
            handled with structured clarity and technical precision.
          </p>

          <p className="text-muted-foreground mb-8">
            We serve artists, record labels and brands across Guwahati and
            Assam, combining creative direction with professional post-
            production systems.
          </p>

          <Link to="/about">
            <Button variant="outline" className="border-white/20 text-white">
              Learn More
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>

        <div>
          <GlassCard className="p-8" hover={false}>
            <img
              src="https://images.unsplash.com/photo-1589178208772-6f239b7652e9?auto=format&fit=crop&w=800&q=80"
              alt="Professional audio and video production studio in Guwahati"
              className="w-full h-80 object-cover rounded-lg"
            />
          </GlassCard>
        </div>
      </div>
    </Section>
  );
};


/* ========================= SERVICES ========================= */

const ServicesSection = () => {
  return (
    <Section id="services" className="bg-black/30">
      <SectionHeading
        caption="Our Services"
        title="Complete Audio & Video Production Services"
        description="End-to-end production services for artists and brands in Guwahati and across Assam."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((service, index) => {
          const Icon = service.icon;
          return (
            <GlassCard key={service.id} delay={index * 0.1} className="p-8">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                <Icon size={24} className="text-primary" />
              </div>

              <h3 className="font-syne text-xl font-semibold text-white mb-3">
                {service.title}
              </h3>

              <p className="text-muted-foreground text-sm mb-4">
                {service.shortDescription}
              </p>

              <Link
                to={
                  service.title === 'Music Videos'
                    ? '/music-video-production-guwahati'
                    : '/services'
                }
                className="text-primary underline text-sm"
              >
                Learn More
              </Link>
            </GlassCard>
          );
        })}
      </div>

      {/* Cluster Links Section */}
      <div className="mt-16 text-center text-muted-foreground text-sm space-y-2">
        <p>
          Explore more:
          {' '}
          <Link to="/music-video-production-process-guwahati" className="underline text-primary">
            Production Process
          </Link>
          {' | '}
          <Link to="/music-video-production-cost-guwahati" className="underline text-primary">
            Cost Guide
          </Link>
          {' | '}
          <Link to="/rap-music-video-production-guwahati" className="underline text-primary">
            Rap Music Videos
          </Link>
          {' | '}
          <Link to="/music-video-production-assam" className="underline text-primary">
            Services Across Assam
          </Link>
        </p>
      </div>
    </Section>
  );
};


const ProcessSection = () => {
  return (
    <Section id="process">
      <SectionHeading
        caption="Our Process"
        title="How We Work"
        description="A structured approach that ensures clarity, quality, and timely delivery on every project."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PROCESS_STEPS.map((step, index) => (
          <motion.div
            key={step.number}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.15 }}
            className="relative"
          >
            {/* Connecting line */}
            {index < PROCESS_STEPS.length - 1 && (
              <div className="hidden lg:block absolute top-12 left-full w-full h-px bg-gradient-to-r from-primary/50 to-transparent z-0" />
            )}

            <div className="relative z-10">
              <span className="font-syne text-7xl font-extrabold text-white/5 absolute -top-4 -left-2">
                {step.number}
              </span>
              <div className="relative pt-8">
                <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/50 flex items-center justify-center mb-4">
                  <span className="font-syne text-sm font-bold text-primary">{step.number}</span>
                </div>
                <h3 className="font-syne text-xl font-semibold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-muted-foreground font-manrope text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

const WhyChooseSection = () => {
  return (
    <Section id="why-choose" className="bg-black/30">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <p className="text-sm uppercase tracking-widest text-primary font-manrope mb-4">
            Why HYDROX
          </p>
          <h2 className="font-syne text-4xl md:text-5xl font-bold text-white leading-tight mb-8">
            Why Choose Us
          </h2>

          <div className="space-y-6">
            {WHY_CHOOSE_US.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center mt-1">
                  <Check size={14} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-syne text-lg font-semibold text-white mb-1">
                    {item.title}
                  </h4>
                  <p className="text-muted-foreground font-manrope text-sm">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="relative">
          <GlassCard className="p-8" hover={false}>
            <img
              src="https://images.unsplash.com/photo-1589178208772-6f239b7652e9?auto=format&fit=crop&w=800&q=80"
              alt="Video editing suite"
              className="w-full h-80 object-cover rounded-lg"
            />
          </GlassCard>
          {/* Decorative element */}
          <div className="absolute -top-4 -left-4 w-24 h-24 border border-primary/30 rounded-lg -z-10" />
        </div>
      </div>
    </Section>
  );
};

const CTASection = () => {
  return (
    <Section id="cta">
      <div className="relative overflow-hidden rounded-2xl glass-heavy p-12 md:p-16 text-center">
        {/* Glow effects */}
        <div className="hero-glow -top-32 -left-32" />
        <div className="hero-glow -bottom-32 -right-32" />

        <div className="relative z-10">
          <h2 className="font-syne text-4xl font-bold text-white mb-6">
        Start Your Audio or Video Project in Guwahati
      </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
        Whether you're planning a music video, commercial shoot or audio
        production project in Assam, Hydrox Studios delivers structured and
        professional execution.
      </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="cta-primary"
                size="lg"
                className="btn-glow bg-primary hover:bg-primary/90 text-white font-manrope px-8 py-6 text-base"
              >
                Get a Quote
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <a
              href={`https://wa.me/${CONTACT_INFO.whatsapp.replace('+', '')}?text=Hi, I'm interested in your services.`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                data-testid="cta-secondary"
                size="lg"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 font-manrope px-8 py-6 text-base"
              >
                WhatsApp Direct
              </Button>
            </a>
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ========================= MAIN ========================= */

export default function Home() {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Hydrox Studios",
    "url": "https://studio.hydroxmusic.com",
    "telephone": CONTACT_INFO.phone,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Guwahati",
      "addressRegion": "Assam",
      "addressCountry": "IN"
    },
    "areaServed": ["Guwahati", "Assam"],
    "sameAs": [CONTACT_INFO.instagram]
  };

  return (
    <div>
      <SEO
        title="Hydrox Studios – Audio & Video Production Studio in Guwahati, Assam"
        description="Hydrox Studios is a professional audio and video production studio in Guwahati, Assam offering music videos, brand films, recording, mixing, mastering and cinematic post-production services across Assam."
        path="/"
        schema={localBusinessSchema}
      />

      <HeroSection />
      <AboutPreviewSection />
      <ServicesSection />
      <ProcessSection />
      <WhyChooseSection />
      <CTASection />
    </div>
  );
}